import { isDirectusError } from '@src/types/errors';

export type FetchErrorHandler = {
  shouldSetServerDown: boolean;
  shouldReject: boolean;
};

/**
 * Handles fetch errors and determines if the error should trigger the "server down" state.
 *
 * This function classifies fetch errors into different categories:
 * - DirectusErrors: Application-level errors that should be rejected but not trigger server down state
 * - AbortErrors: Normal request cancellations (e.g., during navigation) that should be rejected but not trigger server down state
 * - Network errors (TypeError with "Failed to fetch"): Genuine connectivity issues that should trigger server down state
 * - Other errors: Unknown errors that should be rejected but not trigger server down state
 *
 * @param error - The error thrown by fetch
 * @returns Object indicating whether to set server down state and whether to reject the promise
 */
export function handleFetchError(error: Error): FetchErrorHandler {
  if (isDirectusError(error)) {
    console.error('Error fetching data:', error);
    return {
      shouldSetServerDown: false,
      shouldReject: true,
    };
  }

  if (error.name === 'AbortError') {
    console.log('Request aborted (navigation or cancellation)');
    return {
      shouldSetServerDown: false,
      shouldReject: true,
    };
  }

  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    console.error('Network error detected:', error);
    return {
      shouldSetServerDown: true,
      shouldReject: false,
    };
  }

  console.error('Fetch error (not network-related):', error);
  return {
    shouldSetServerDown: false,
    shouldReject: true,
  };
}
